import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Briefcase,
  Globe2,
  GraduationCap,
  ImagePlus,
  Info,
  Mail,
  Save,
  Trash2,
  User,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { phoneProblem } from "../../utils/phone";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import { useBranch } from "../../contexts/BranchContext";
import { usePlan } from "../../contexts/PlanContext";
import { usePlanUsage, isOverLimit } from "../../hooks/usePlanUsage";
import {
  AppButton,
  AppCard,
  AppCardContent,
  AppCardDescription,
  AppCardHeader,
  AppCardTitle,
  Callout,
  Checkbox,
  FormActionBar,
  FormField,
  Input,
  NativeSelect,
  PageHeader,
  PhoneInput,
  ResetButton,
  Textarea,
  UserAvatar,
} from "../ui";

const GENDERS = [
  { value: "", label: "Select gender" },
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

// ⚠️ The values carry the apostrophe: they are what lands in
// `Instructor.qualification`.
const QUALIFICATIONS = [
  { value: "", label: "Select qualification" },
  { value: "Bachelor's", label: "Bachelor's" },
  { value: "Master's", label: "Master's" },
  { value: "PhD", label: "PhD" },
  { value: "Other", label: "Other" },
];

const BIO_MAX = 300;
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const AVATAR_ACCEPT = "image/jpeg,image/png,image/webp";

const today = () => new Date().toISOString().slice(0, 10);

/** API dates arrive as ISO strings; a date input wants YYYY-MM-DD. */
const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

/**
 * A saved value the fixed list does not know (an older free-text entry) is
 * offered as an extra option, so the select shows it instead of a blank.
 */
const withSavedOption = (options, value) =>
  !value || options.some((o) => o.value === value) ? options : [...options, { value, label: value }];

const emptyForm = () => ({
  name: "",
  email: "",
  mobile: "",
  gender: "",
  dob: "",
  address: "",
  qualification: "",
  experience: "",
  subject: "",
  otherSkills: "",
  linkedin: "",
  joinedDate: today(),
  about: "",
  active: true,
  branches: [],
  showOnWebsite: false,
});

const fromInstructor = (i) => ({
  name: i.name || "",
  email: i.email || "",
  mobile: i.mobile || "",
  gender: i.gender || "",
  dob: toDateInput(i.dob),
  address: i.address || "",
  qualification: i.qualification || "",
  experience: i.experience == null ? "" : String(i.experience),
  subject: i.subject || "",
  otherSkills: i.otherSkills || "",
  linkedin: i.linkedin || "",
  joinedDate: toDateInput(i.joinedDate),
  about: i.about || "",
  // Older rows carry only `status`; the list page badges that, so it wins
  // when `active` was never written.
  active: typeof i.active === "boolean" ? i.active : i.status !== "Inactive",
  branches: (i.branches || []).map((b) => String(b?._id || b)),
  showOnWebsite: Boolean(i.showOnWebsite),
});

/**
 * Validation runs on submit and then clears per field as it is corrected, so a
 * fixed field stops shouting before the next submit.
 *
 * Mobile is checked against its own country (utils/phone), and only when it
 * changed: an instructor saved under the old 7-15-digit rule must still save
 * when someone edits their bio.
 */
const validate = (form, savedMobile = "") => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Enter the instructor's full name.";

  if (!form.email.trim()) errors.email = "Email is required — it is their sign-in id.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
    errors.email = "That does not look like a valid email address.";

  if (!form.mobile.trim()) errors.mobile = "Mobile number is required.";
  else if (form.mobile !== savedMobile) {
    const problem = phoneProblem(form.mobile);
    if (problem) errors.mobile = problem;
  }

  if (form.dob && form.dob > today()) errors.dob = "Date of birth cannot be in the future.";

  if (form.experience !== "") {
    const years = Number(form.experience);
    if (!Number.isFinite(years) || years < 0 || years > 70)
      errors.experience = "Enter years of experience between 0 and 70.";
  }

  if (form.linkedin.trim() && !/^https?:\/\/.+\..+/.test(form.linkedin.trim()))
    errors.linkedin = "Enter a full URL, starting with https://";

  if (form.about.length > BIO_MAX) errors.about = `Keep the bio under ${BIO_MAX} characters.`;

  return errors;
};

/**
 * AddOrEditInstructorPage — onboard an instructor, or edit one. One page for
 * both, so a field added to one can never be missing from the other.
 *
 * ⚠️ The avatar is uploaded to storage the moment it is picked and only its URL
 * travels with the form. `Instructor.avatar` is a String; a `File` in the JSON
 * body serialises to `{}` and makes Mongoose reject the save.
 *
 * ⚠️ When editing, the avatar is sent ONLY if it was changed on this page. The
 * profile endpoint substitutes a short-lived signed URL for the photo an
 * instructor set on their own account, and saving that back would store a
 * link that stops working within the hour.
 */
const AddOrEditInstructorPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { branches, isMultiBranch } = useBranch();
  const { hasPlanFeature } = usePlan();
  const hasWebsite = hasPlanFeature("cmsWebsite");
  const { usage } = usePlanUsage();
  const instructorLimit = isOverLimit(usage?.instructors);
  // The cap stops a new instructor, not a correction to an existing one.
  const limitReached = !isEdit && instructorLimit.overLimit;

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState("");
  const [hasLogin, setHasLogin] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // { url, key } — `key` is set only for a photo uploaded on this page, so a
  // photo the admin then discards is deleted rather than orphaned in storage.
  const [avatar, setAvatar] = useState(null);
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);
  const pristineRef = useRef({ form: emptyForm(), avatar: null, name: "" });

  const activeBranches = useMemo(
    () => branches.filter((b) => b.isActive !== false),
    [branches]
  );
  const showBranchPicker = isMultiBranch && activeBranches.length > 0;
  const backPath = isEdit ? `/instructors/${id}` : "/instructors";

  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  // Both routes render this component, and the router keeps the instance when
  // moving between them — so arriving at "add" from an edit must start blank.
  useEffect(() => {
    if (!isEdit) {
      pristineRef.current = { form: emptyForm(), avatar: null, name: "" };
      setForm(emptyForm());
      setAvatar(null);
      setAvatarChanged(false);
      setErrors({});
      setHasLogin(false);
      setLoadError("");
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const { data } = await apiClient.get(`/instructors/${id}`);
        if (cancelled) return;
        const instructor = data?.instructor || {};
        const loaded = fromInstructor(instructor);
        const loadedAvatar = instructor.avatar ? { url: instructor.avatar, key: null } : null;
        pristineRef.current = { form: loaded, avatar: loadedAvatar, name: loaded.name };
        setForm(loaded);
        setAvatar(loadedAvatar);
        setAvatarChanged(false);
        setErrors({});
        setHasLogin(Boolean(instructor.userId) || instructor.accountStatus === "active");
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.response?.status === 404
            ? "This instructor no longer exists."
            : "Could not load this instructor. Refresh the page to try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleChange = (e) => setField(e.target.name, e.target.value);

  const toggleBranch = (branchId) =>
    setForm((prev) => ({
      ...prev,
      branches: prev.branches.includes(branchId)
        ? prev.branches.filter((b) => b !== branchId)
        : [...prev.branches, branchId],
    }));

  // A photo uploaded on this page and then dropped is deleted, not orphaned.
  // The saved photo (no key) is never touched here — the save decides that.
  const discardUploadedAvatar = (photo) => {
    if (!photo?.key) return;
    apiClient.delete("/files/delete", { data: { key: photo.key } }).catch(() => {
      /* The saved profile never referenced it, so an orphan is not worth a toast. */
    });
  };

  const handleReset = () => {
    const pristine = pristineRef.current;
    if (avatar !== pristine.avatar) discardUploadedAvatar(avatar);
    setForm(pristine.form);
    setAvatar(pristine.avatar);
    setAvatarChanged(false);
    setErrors({});
  };

  // ── Avatar ────────────────────────────────────────────────────────────────
  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Choose an image file for the profile photo.", "error");
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      showToast("The photo must be smaller than 5 MB.", "error");
      return;
    }

    setAvatarUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("entity", "instructors");
      body.append("type", "avatar");
      const { data: uploaded } = await apiClient.post("/files/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // `Instructor.avatar` holds a URL, not a storage key — every reader of it
      // (the profile card, the trainer grid, the website) renders it straight
      // into an <img>. With `R2_PUBLIC_URL` configured this is the permanent CDN
      // path; without one it is a presigned URL that eventually 403s, which is a
      // storage-config question, not something this page can fix.
      const { data: read } = await apiClient.get(
        `/files/presign-get?key=${encodeURIComponent(uploaded.key)}`
      );
      discardUploadedAvatar(avatar);
      setAvatar({ url: read.url, key: uploaded.key });
      setAvatarChanged(true);
    } catch (err) {
      showToast(err?.response?.data?.error || "Could not upload the photo.", "error");
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarRemove = () => {
    discardUploadedAvatar(avatar);
    setAvatar(null);
    setAvatarChanged(true);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || limitReached) return;

    const found = validate(form, pristineRef.current.form.mobile);
    setErrors(found);
    if (Object.keys(found).length) {
      showToast("Check the highlighted fields and try again.", "error");
      document.querySelector(`[name="${Object.keys(found)[0]}"]`)?.focus();
      return;
    }

    // Built explicitly rather than spread, for both modes: the server writes
    // only these fields, and a form that echoed back the whole loaded document
    // is how the old edit page sent a photo as `{}`.
    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      mobile: form.mobile.trim(),
      gender: form.gender,
      address: form.address.trim(),
      qualification: form.qualification,
      experience: form.experience,
      subject: form.subject.trim(),
      otherSkills: form.otherSkills.trim(),
      linkedin: form.linkedin.trim(),
      about: form.about.trim(),
      active: form.active,
      // The list page badges `status`, not `active`. Writing only one of the two
      // is how a deactivated instructor kept showing a green "Active" chip.
      status: form.active ? "Active" : "Inactive",
    };
    if (!isEdit || avatarChanged) payload.avatar = avatar?.url || "";
    if (isEdit) {
      // Clearing a date has to reach the server as a clear.
      payload.dob = form.dob || null;
      payload.joinedDate = form.joinedDate || null;
    } else {
      // An empty date has to be ABSENT here, not null, or it overrides the
      // schema default instead of falling back to it.
      if (form.dob) payload.dob = form.dob;
      if (form.joinedDate) payload.joinedDate = form.joinedDate;
    }
    if (showBranchPicker) payload.branches = form.branches;
    if (hasWebsite) payload.showOnWebsite = form.showOnWebsite;

    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.put(`/instructors/${id}`, payload);
        showToast("Instructor updated.");
        setTimeout(() => navigate(`/instructors/${id}`), 1200);
      } else {
        await apiClient.post("/instructors/add", payload);
        showToast("Instructor added. Their welcome email is on its way.");
        setTimeout(() => navigate("/instructors"), 1200);
      }
    } catch (err) {
      const message = err?.response?.data?.message;

      if (err?.response?.status === 409) {
        const clash = "Another instructor already uses this email or mobile number.";
        setErrors((prev) => ({ ...prev, email: clash, mobile: clash }));
        showToast(clash, "error");
      } else if (!err?.response?.data?.planLimit) {
        // A plan-limit 403 is already surfaced globally by apiClient; a second
        // toast for it would only talk over the upgrade prompt.
        showToast(message || (isEdit ? "Failed to update instructor." : "Failed to add instructor."), "error");
      }
      setSaving(false);
    }
  };

  const title = isEdit ? "Edit Instructor" : "Add New Instructor";
  const header = (
    <PageHeader
      icon={GraduationCap}
      title={title}
      description={isEdit
        ? `Update ${pristineRef.current.name || "this instructor"}'s profile and access`
        : "Create a trainer profile and send them their sign-in details"}
      backButton
      onBack={() => navigate(backPath)}
    />
  );

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header}
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-[minmax(0,1fr)_320px]">
          <Shimmer height={300} count={2} />
          <Shimmer height={200} count={2} />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header}
        <Callout tone="danger" role="alert">{loadError}</Callout>
      </div>
    );
  }

  const bioLeft = BIO_MAX - form.about.length;

  return (
    <form
      className="tw-flex tw-flex-col tw-gap-4 has-form-actionbar"
      onSubmit={handleSubmit}
      noValidate
    >
      {header}

      {limitReached && (
        <Callout tone="warning">
          Your plan allows {instructorLimit.max} instructors and {instructorLimit.used} are already
          on it. Upgrade your plan, or deactivate an existing instructor, to add another.
        </Callout>
      )}

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-[minmax(0,1fr)_320px]">
        {/* ── Main column ───────────────────────────────────────────────── */}
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
          <AppCard>
            <AppCardHeader>
              <AppCardTitle className="tw-flex tw-items-center tw-gap-2">
                <User size={16} aria-hidden="true" /> Personal details
              </AppCardTitle>
              <AppCardDescription>
                The email and mobile are how this person signs in and hears from you.
              </AppCardDescription>
            </AppCardHeader>
            <AppCardContent>
              <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                <FormField label="Full name" required error={errors.name}>
                  <Input
                    name="name"
                    size="lg"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. Priya Raman"
                    autoComplete="name"
                  />
                </FormField>

                <FormField
                  label="Email address"
                  required
                  error={errors.email}
                  hint={isEdit && hasLogin
                    ? "Changing this updates their profile only. They keep signing in with the email on their login account."
                    : undefined}
                >
                  <Input
                    name="email"
                    type="email"
                    size="lg"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="name@institute.com"
                    autoComplete="email"
                    leading={<Mail size={15} aria-hidden="true" />}
                  />
                </FormField>

                <FormField label="Mobile number" required error={errors.mobile}>
                  <PhoneInput
                    name="mobile"
                    size="lg"
                    value={form.mobile}
                    onChange={(value) => setField("mobile", value)}
                    placeholder="9876543210"
                    nativeValidation={false}
                  />
                </FormField>

                <FormField label="Date of birth" error={errors.dob}>
                  <Input
                    name="dob"
                    type="date"
                    size="lg"
                    max={today()}
                    value={form.dob}
                    onChange={handleChange}
                  />
                </FormField>

                <FormField label="Gender">
                  <NativeSelect
                    name="gender"
                    size="lg"
                    options={withSavedOption(GENDERS, form.gender)}
                    value={form.gender}
                    onChange={handleChange}
                  />
                </FormField>

                <FormField label="Address" className="sm:tw-col-span-2">
                  <Textarea
                    name="address"
                    size="lg"
                    rows={2}
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Street, city, state and postcode"
                  />
                </FormField>
              </div>
            </AppCardContent>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <AppCardTitle className="tw-flex tw-items-center tw-gap-2">
                <Briefcase size={16} aria-hidden="true" /> Professional details
              </AppCardTitle>
              <AppCardDescription>
                What they teach, and what students read on their trainer profile.
              </AppCardDescription>
            </AppCardHeader>
            <AppCardContent>
              <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                <FormField label="Highest qualification">
                  <NativeSelect
                    name="qualification"
                    size="lg"
                    options={withSavedOption(QUALIFICATIONS, form.qualification)}
                    value={form.qualification}
                    onChange={handleChange}
                  />
                </FormField>

                <FormField label="Years of experience" error={errors.experience}>
                  <Input
                    name="experience"
                    type="number"
                    size="lg"
                    min={0}
                    max={70}
                    step={1}
                    value={form.experience}
                    onChange={handleChange}
                    placeholder="e.g. 6"
                  />
                </FormField>

                <FormField
                  label="Primary subject / skill"
                  hint="Shown under their name on the trainer list."
                >
                  <Input
                    name="subject"
                    size="lg"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="e.g. Full Stack Java"
                  />
                </FormField>

                <FormField label="Other skills" hint="Separate each one with a comma.">
                  <Input
                    name="otherSkills"
                    size="lg"
                    value={form.otherSkills}
                    onChange={handleChange}
                    placeholder="React, Node.js, Python"
                  />
                </FormField>

                <FormField label="LinkedIn / profile link" error={errors.linkedin}>
                  <Input
                    name="linkedin"
                    type="url"
                    size="lg"
                    value={form.linkedin}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/username"
                  />
                </FormField>

                <FormField label="Joined date" hint={isEdit ? undefined : "Defaults to today."}>
                  <Input
                    name="joinedDate"
                    type="date"
                    size="lg"
                    value={form.joinedDate}
                    onChange={handleChange}
                  />
                </FormField>

                <div className="tw-flex tw-flex-col tw-gap-1 sm:tw-col-span-2">
                  <FormField label="About / bio" error={errors.about}>
                    <Textarea
                      name="about"
                      size="lg"
                      rows={4}
                      maxLength={BIO_MAX}
                      value={form.about}
                      onChange={handleChange}
                      placeholder="A short introduction students will read on this trainer's profile."
                    />
                  </FormField>
                  <span className="tw-self-end tw-text-xs tw-text-content-subtle">
                    {bioLeft} characters left
                  </span>
                </div>
              </div>
            </AppCardContent>
          </AppCard>
        </div>

        {/* ── Side column ───────────────────────────────────────────────── */}
        <aside className="tw-flex tw-flex-col tw-gap-4 xl:tw-sticky xl:tw-top-20 xl:tw-self-start">
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Profile photo</AppCardTitle>
              <AppCardDescription>JPG, PNG or WebP, up to 5 MB.</AppCardDescription>
            </AppCardHeader>
            <AppCardContent>
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                <UserAvatar
                  name={form.name || "New Instructor"}
                  src={avatar?.url}
                  size="xl"
                  bordered
                />
                <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
                  <AppButton
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={avatarUploading}
                    onClick={() => avatarInputRef.current?.click()}
                  >
                    <ImagePlus size={16} />
                    {avatarUploading ? "Uploading..." : avatar ? "Change photo" : "Upload photo"}
                  </AppButton>
                  {avatar && !avatarUploading && (
                    <AppButton
                      type="button"
                      variant="ghost-destructive"
                      size="sm"
                      onClick={handleAvatarRemove}
                    >
                      <Trash2 size={16} /> Remove
                    </AppButton>
                  )}
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept={AVATAR_ACCEPT}
                  onChange={handleAvatarPick}
                  className="tw-hidden"
                  tabIndex={-1}
                  aria-hidden="true"
                />
                {!avatar && (
                  <p className="tw-m-0 tw-text-center tw-text-xs tw-text-content-subtle">
                    Without a photo we show their initials.
                  </p>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Account</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="tw-flex tw-flex-col tw-gap-4">
                {/* A Checkbox, not a Switch: this is saved when the form is
                    submitted, not applied the moment it is toggled. */}
                <Checkbox
                  checked={form.active}
                  onChange={(e) => setField("active", e.target.checked)}
                  label="Active instructor"
                  description="Inactive trainers cannot be assigned to batches."
                />

                {showBranchPicker && (
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                      Branch access
                    </span>
                    <div className="tw-flex tw-max-h-56 tw-flex-col tw-gap-2 tw-overflow-y-auto">
                      {activeBranches.map((branch) => (
                        <Checkbox
                          key={branch._id}
                          label={branch.name}
                          checked={form.branches.includes(String(branch._id))}
                          onChange={() => toggleBranch(String(branch._id))}
                        />
                      ))}
                    </div>
                    <span className="tw-text-xs tw-text-content-subtle">
                      {form.branches.length === 0
                        ? "No branch selected — they can work across every branch."
                        : `Limited to ${form.branches.length} of ${activeBranches.length} branches.`}
                    </span>
                  </div>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          {hasWebsite && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle className="tw-flex tw-items-center tw-gap-2">
                  <Globe2 size={16} aria-hidden="true" /> Website
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
                <Checkbox
                  checked={form.showOnWebsite}
                  onChange={(e) => setField("showOnWebsite", e.target.checked)}
                  label="Show on your website"
                  description="Lists them, with their photo, subject, experience and bio, in the trainers section of your website's About page."
                />
                {/* The two directions people confuse: this page grants LMS
                    access, the Team tab does not. Saying so here is what keeps
                    an admin from adding a guest mentor as an instructor — which
                    would give them a login and a seat on the plan. */}
                <span className="tw-text-xs tw-text-content-subtle">
                  Want different wording, skills or a different photo on the website? Write it on{" "}
                  <Link to="/cms?tab=team" className="tw-text-primary tw-underline">
                    Website → Team
                  </Link>
                  , where you can also add a mentor who should have no LMS login at all.
                </span>
              </AppCardContent>
            </AppCard>
          )}

          {!isEdit && (
            <Callout tone="info" icon={<Info size={14} aria-hidden="true" />}>
              On save we email a welcome message with their sign-in link, and send the same welcome
              over WhatsApp when a mobile number is set.
            </Callout>
          )}
        </aside>
      </div>

      <FormActionBar
        info={
          !isEdit && instructorLimit.max > 0
            ? `${instructorLimit.used} of ${instructorLimit.max} instructors used`
            : null
        }
      >
        <AppButton type="button" variant="outline" onClick={() => navigate(backPath)}>
          Cancel
        </AppButton>
        <ResetButton onReset={handleReset} disabled={saving} />
        <AppButton
          type="submit"
          variant="default"
          disabled={saving || avatarUploading || limitReached}
        >
          <Save size={16} />
          {saving ? "Saving..." : isEdit ? "Save Changes" : "Save Instructor"}
        </AppButton>
      </FormActionBar>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </form>
  );
};

export default AddOrEditInstructorPage;
